import { Container } from '@acies/core';
import { Flag, Service, Variant } from '@acies/domain';
import { CheckBox, CheckBoxEvent, HTMLSelectListEvent } from '@acies/ui';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from '@shared/services/storage.service';
import { UserService } from '@shared/services/user.service';
import { FeatureFlagsAdminModel } from '../model/model';
import { FlagsService } from '../services/flags.service';
import { FlagListItemRenderer } from '../view/flag-list-item-renderer';
import { AccessController } from './access.controller';
import { FlagsController } from './flags.controller';
import { VariantsController } from './variants.controller';

export class FeatureFlagsAdminController {
	public flagsController: FlagsController;
	public variantsController: VariantsController;
	#flagsService: FlagsService;
	#accessController: AccessController;
	#model: FeatureFlagsAdminModel;
	#translator: TranslateService;
	public constructor(sessionStorageService: SessionStorageService, userService: UserService, httpClient: HttpClient, model: FeatureFlagsAdminModel, translator: TranslateService) {
		this.#model = model;
		this.#translator = translator;
		this.#flagsService = new FlagsService(httpClient, sessionStorageService);
		this.#accessController = new AccessController(sessionStorageService, userService, model);
		this.variantsController = new VariantsController(this.#flagsService, model);
		this.flagsController = new FlagsController(this.#flagsService, model);
		this.#loadAccess();
	}

	/** We place the enabledChanged event handler in the top level controller, so we avoid having the same business logic in the variant- and flags controllers. */
	public enabledChanged(event: Event): void {
		if (event instanceof CheckBoxEvent) {
			this.#selectIfFlagChecked(event);
			this.#updateFlagOrVariantEnabled(event.item, event.selected);
		}
	}

	/**
	 * A simple way to simulate a user selection of the flag item renderer that has the checkbox as a nested child is to call the synchronous click() method.
	 *
	 * Since the checkbox can have two different UI renderers, we check if the parentElement is an instance of the FlagListItemRenderer to be absolutely sure and call click() to simulate that the user has selected the checkbox parent renderer.
	 */
	#selectIfFlagChecked(event: CheckBoxEvent<Flag | Variant>): void {
		if (event.target instanceof CheckBox) {
			const checkBox = event.target;
			// the checkbox is located inside a container, so get a reference to that DOM element.
			if (checkBox.parentElement instanceof Container) {
				const container = checkBox.parentElement;
				// the container has the renderer as a parent so check for that.
				if (container.parentElement instanceof FlagListItemRenderer) {
					const itemRenderer = container.parentElement;
					// if the renderer is already selected, don't click
					// since that would de-select the renderer and empty
					// the variants list.
					if (itemRenderer.selected === false) {
						itemRenderer.click();
					}
				}
			}
		}
	}

	/** When updating the enabled property on a Flag or Variant instance, we set a reference to that instance so we can revert later of the service request fails. */
	#updateFlagOrVariantEnabled(instance: Flag | Variant, enabled: boolean) {
		this.#model.updatingInstance = instance;
		this.#model.updatingInstance.enabled = enabled;
		const enabledString = this.#translator.currentLang === 'da' ? (enabled ? 'TIL' : 'FRA') : enabled ? 'ON' : 'OFF';
		const name = instance instanceof Flag ? instance.name : instance.account.name;
		this.#model.confirmBody = this.#translator.instant(`setup.featureflagsadmin.confirmBody`, {
			name,
			enabled: enabledString,
		});
		this.#model.showConfirmationDialog();
	}

	public serviceSelectionChanged(event: Event): void {
		if (event instanceof HTMLSelectListEvent && event.selectedItem instanceof Service) {
			this.#model.selectedService = event.selectedItem;
		} else {
			this.#model.selectedService = null;
		}
	}

	/** Start with loading the Access data so we get Organizations set in the Data Model. */
	#loadAccess(): void {
		this.#accessController.loadAccess().subscribe((value) => {
			if (value) {
				this.#loadFeatureFlags();
			} else {
				this.#model.globalSpinnerVisible = false;
				this.#model.displayErrorDialogFromError();
			}
		});
	}

	#loadFeatureFlags(): void {
		this.flagsController.loadFlags().subscribe((value) => {
			if (value instanceof Error) {
				this.#model.displayErrorDialogFromError(value);
			} else {
				this.#model.services = value;
				this.#model.globalSpinnerVisible = false;
				this.#model.featureFlagsUIVisible = true;
			}
		});
	}
}
